import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { mapBy, pick, queue } from '@nullvoxpopuli/ember-composable-helpers';
import type { DetailedChangeset } from 'ember-changeset/types';
import PowerSelect from 'ember-power-select/components/power-select';
import set from 'ember-set-helper/helpers/set';
import { and, not } from 'ember-truth-helpers';
import removeYour from 'garaje/helpers/remove-your';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import { DISCARDED_RESPONSE_STRINGS } from 'garaje/utils/enums';

interface EntriesInputFieldSignature {
  Args: {
    changeset: DetailedChangeset<SignInFieldModel>;
    update: () => void;
    disabled?: boolean;
    isReadonly?: boolean;
    label?: string;
  };
  Element: HTMLDivElement;
}

export default class EntriesInputField extends Component<EntriesInputFieldSignature> {
  @tracked isFocused = false;

  get displayValue(): string | null | undefined {
    if (this.args.changeset.value === DISCARDED_RESPONSE_STRINGS.PLACEHOLDER) {
      return DISCARDED_RESPONSE_STRINGS.DISPLAY_TEXT;
    }

    return this.args.changeset.value;
  }

  get isDisabled(): boolean {
    return (
      this.args.disabled || this.args.isReadonly || this.args.changeset.value === DISCARDED_RESPONSE_STRINGS.PLACEHOLDER
    );
  }

  get isRequired(): boolean {
    return this.args.changeset.required && !this.args.changeset.allowVisitorRespondents;
  }

  get errorValidation(): string[] {
    return <string[]>this.args.changeset.error?.['value']?.['validation'] || [];
  }

  <template>
    <div data-test-flow-field={{@changeset.name}} ...attributes>
      <label for={{@changeset.id}} class={{if @isReadonly "cursor-default"}}>
        {{#if @label}}
          {{@label}}
        {{else}}
          {{removeYour (if @changeset.localized @changeset.localized @changeset.name)}}
        {{/if}}
        {{#if this.isRequired}}
          <span class="requiredField" data-test-required-field></span>
        {{/if}}
      </label>
      {{#if @changeset.isSingleSelection}}
        <div style="margin-bottom:20px;" class="w-full" data-test-option-select>
          <PowerSelect
            @options={{mapBy "value" @changeset.options}}
            @selected={{this.displayValue}}
            @placeholder="Choose an option"
            @disabled={{this.isDisabled}}
            @allowClear={{true}}
            @searchEnabled={{true}}
            @triggerClass={{if this.errorValidation "error"}}
            @onChange={{queue (set @changeset "value") (fn @update @changeset)}}
            as |option|
          >
            {{option}}
          </PowerSelect>
        </div>
      {{else}}
        <input
          data-filter
          data-private
          id={{@changeset.id}}
          type="text"
          value={{this.displayValue}}
          disabled={{this.isDisabled}}
          class="{{if (and this.errorValidation (not this.isFocused)) 'error'}} {{if @isReadonly 'readonly'}} "
          readonly={{@isReadonly}}
          {{on "focusin" (set this "isFocused" true)}}
          {{on "focusout" (set this "isFocused" false)}}
          {{on "input" (pick "target.value" (queue (set @changeset "value") (fn @update @changeset)))}}
        />
      {{/if}}

      {{#unless this.isFocused}}
        {{#each this.errorValidation as |errorKey|}}
          <div class="bottom-error pt-1" data-test-error>{{errorKey}}</div>
        {{/each}}
      {{/unless}}
    </div>
  </template>
}
